import { useCallback, useEffect, useRef, useState } from "react";
import agentChatService, { AgentRequestError } from "api/agentChat/agentChat.service";
import type { IAgentThread } from "api/agentChat/agentChat.type";

/**
 * The user's conversations with the design agent, newest first — the sidebar's list.
 *
 * Titles are the agent's own (its title generation writes them in the background), so the list is
 * re-read after every finished turn (`refresh`) rather than guessed here; the open conversation's thread stream also
 * says its title (`applyTitle`), so a new or renamed title shows without waiting for that read. `available` is false while
 * the agent build has no thread routes yet (404/405): the sidebar then shows only the open chat.
 */
/** Sidebar page size: the list grows by this many as the user scrolls to its end. */
export const THREAD_PAGE_SIZE = 10;
/** A canvas's chats in its switcher: one read, the agent's page cap. */
const CANVAS_THREADS_MAX = 100;

export interface AgentThreads {
  threads: IAgentThread[];
  loading: boolean;
  /** The agent has older conversations than the ones loaded. */
  hasMore: boolean;
  loadingMore: boolean;
  /** Load the next page of older conversations (no-op while loading or when there are none). */
  loadMore: () => Promise<void>;
  available: boolean;
  refresh: () => Promise<void>;
  /** The canvas's latest conversation, if it has one (the agent lists a canvas's chats newest first). */
  findByCanvas: (canvasId: string) => Promise<IAgentThread | undefined>;
  /** All of a canvas's conversations, newest first — the chat switcher's list. Empty when the agent cannot say. */
  listByCanvas: (canvasId: string) => Promise<IAgentThread[]>;
  /**
   * Bind a conversation to its canvas. A canvas holds many chats; a chat belongs to one canvas for good: the same
   * canvas again is a no-op on the agent, another canvas is its 409 `{canvasId}` — the binding the agent holds stands,
   * and that canvas is returned so the caller opens the chat there. Null otherwise.
   */
  bind: (threadId: string, canvasId: string) => Promise<string | null>;
  rename: (threadId: string, title: string) => Promise<void>;
  /**
   * The agent said a conversation's title on its thread stream (`thread {title}`): the list shows it at once, without a
   * re-read. A mirror of the agent's word, not a guess; a conversation not loaded yet is left to the next read.
   */
  applyTitle: (threadId: string, title: string) => void;
}

/**
 * Several surfaces hold this list at once (the chat's history menu, the main menu's Recent chats). A
 * fetch in one is announced so the others re-read the same span; a re-read caused by an announcement is
 * not announced again, so two lists never ping-pong.
 */
const THREADS_CHANGED = "refabric:agent-threads-changed";

export const useAgentThreads = ({ enabled = true }: { enabled?: boolean } = {}): AgentThreads => {
  const [threads, setThreads] = useState<IAgentThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // How many conversations the list shows: a refresh re-reads the same span so loaded pages stay.
  const shown = useRef(THREAD_PAGE_SIZE);
  // This list's identity in the change announcements: its own fetch must not re-read it.
  const instance = useRef({});
  const mounted = useRef(true);
  // Set in the effect body as well as cleared in its cleanup: StrictMode unmounts and remounts once,
  // and a ref only cleared would stay "unmounted" for the instance that actually lives.
  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const reread = useCallback(async (announce: boolean) => {
    if (!agentChatService.isConfigured()) {
      setAvailable(false);
      setLoading(false);

      return;
    }
    try {
      const response = await agentChatService.listThreads({ perPage: shown.current });
      if (!mounted.current) return;
      setThreads(response.threads ?? []);
      setHasMore(Boolean(response.hasMore));
      setAvailable(true);
      if (announce) window.dispatchEvent(new CustomEvent(THREADS_CHANGED, { detail: instance.current }));
    } catch (failure) {
      if (!mounted.current) return;
      // 403: the agent's own gate (`CHAT_NOT_ENABLED`) — the chat is not this account's; the list stays quietly empty.
      if (failure instanceof AgentRequestError && (failure.status === 403 || failure.status === 404 || failure.status === 405)) setAvailable(false);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  const refresh = useCallback(() => reread(true), [reread]);

  useEffect(() => {
    if (!enabled) return;
    void reread(false);
    const onChanged = (event: Event) => {
      if ((event as CustomEvent).detail !== instance.current) void reread(false);
    };
    window.addEventListener(THREADS_CHANGED, onChanged);

    return () => window.removeEventListener(THREADS_CHANGED, onChanged);
  }, [enabled, reread]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      // Page of the same size right after the loaded span: the agent lists newest first.
      const page = Math.ceil(shown.current / THREAD_PAGE_SIZE);
      const response = await agentChatService.listThreads({ page, perPage: THREAD_PAGE_SIZE });
      if (!mounted.current) return;
      shown.current = (page + 1) * THREAD_PAGE_SIZE;
      setThreads((current) => {
        const seen = new Set(current.map((thread) => thread.threadId));

        return [...current, ...(response.threads ?? []).filter((thread) => !seen.has(thread.threadId))];
      });
      setHasMore(Boolean(response.hasMore));
    } catch {
      // The next scroll to the end tries again.
    } finally {
      if (mounted.current) setLoadingMore(false);
    }
  }, [hasMore, loadingMore]);

  const findByCanvas = useCallback(async (canvasId: string) => {
    if (!agentChatService.isConfigured()) return undefined;
    try {
      const response = await agentChatService.listThreads({ canvasId, perPage: 1 });

      return (response.threads ?? []).find((thread) => thread.canvasId === canvasId);
    } catch {
      return undefined;
    }
  }, []);

  const listByCanvas = useCallback(async (canvasId: string) => {
    if (!agentChatService.isConfigured()) return [];
    try {
      const response = await agentChatService.listThreads({ canvasId, perPage: CANVAS_THREADS_MAX });

      return (response.threads ?? []).filter((thread) => thread.canvasId === canvasId);
    } catch {
      return [];
    }
  }, []);

  const bind = useCallback(
    async (threadId: string, canvasId: string) => {
      let boundElsewhere: string | null = null;
      try {
        await agentChatService.bindThread({ threadId, canvasId });
      } catch (failure) {
        const owner = failure instanceof AgentRequestError && failure.status === 409 ? failure.extra.canvasId : undefined;
        if (typeof owner === "string" && owner && owner !== canvasId) boundElsewhere = owner;
        // Any other failure: the binding lives in the URL (`?thread=`) until the agent can store it.
      }
      // The list catches up on its own: the first message does not wait for it (P4 #1, 0.5 s of the first send).
      void refresh();

      return boundElsewhere;
    },
    [refresh]
  );

  const rename = useCallback(
    async (threadId: string, title: string) => {
      setThreads((current) => current.map((thread) => (thread.threadId === threadId ? { ...thread, title } : thread)));
      try {
        await agentChatService.renameThread(threadId, { title });
      } catch {
        // The re-read below puts the stored title back: a failed rename is visible as no rename.
      }
      await refresh();
    },
    [refresh]
  );

  const applyTitle = useCallback((threadId: string, title: string) => {
    setThreads((current) =>
      current.some((thread) => thread.threadId === threadId && thread.title !== title) ? current.map((thread) => (thread.threadId === threadId ? { ...thread, title } : thread)) : current
    );
  }, []);

  return { threads, loading, hasMore, loadingMore, loadMore, available, refresh, findByCanvas, listByCanvas, bind, rename, applyTitle };
};
